import type { FC } from "react";
import { Link } from "react-router";
import { normalizeOrderCode } from "../../../libs/mypage/orderCode";
import {
  COMPANY_INFO,
  flattenItemsForReceipt,
  formatReceiptDate,
  storeLogo,
} from "../../../libs/mypage/receipt";
import { Button } from "../components/Button";
import { Container } from "../components/Container";
import { formatYen, type OrderDetailDTO, type OrderItemDTO } from "../components/format";
import { useDecodedOrderCode } from "../hooks/useDecodedOrderCode";
import { useOrder } from "../hooks/useOrder";

/**
 * 領収書ページ。`window.print()` ベースの印刷プレビュー (Cloudflare Browser Rendering で
 * PDF 化する案も将来候補だが現状はブラウザの印刷ダイアログ任せ)。
 *
 * 印刷時挙動:
 *  - SiteHeader / SiteFooter は `data-print-hide` 属性で隠す
 *  - ツールバー (印刷ボタン / 戻るリンク) は `print:hidden`
 *  - 商品行はモバイルカード (sm 未満) を `print:hidden` で消し、テーブルを `print:table` で出す
 *  - `tr` / サマリ / フッタ は `break-inside: avoid` でページ跨ぎを避ける
 *
 * 適格請求書の必須項目:
 *  - 適格請求書発行事業者の氏名 (`COMPANY_INFO.legalName`) と登録番号 (`invoiceRegistrationNumber`)
 *  - 取引年月日 (`order.createdAt`)
 *  - 取引内容 (商品テーブル)
 *  - **税率ごとに区分して合計した対価の額** + **税率ごとに区分した消費税額** (サマリ)
 *  - 受領者の氏名 — Web 領収書のためフォーム手書き欄に振る
 *
 * NOTE 行ごとの「消費税」「合計」列は出さない (`Math.round` による累積誤差で `taxJpy` と
 * 縦合計がズレる / line-item discount があると行合計の和 ≠ 小計 になり領収書として誤解を
 * 招くため。法的に正確な金額・税額はサマリ部のみで主張する)。
 */
export const ReceiptPage: FC = () => {
  const code = useDecodedOrderCode();
  const result = useOrder(code);

  if (result.status === "loading") {
    return (
      <Container>
        <p className="py-8 text-center text-neutral-500 text-sm">読み込み中…</p>
      </Container>
    );
  }
  if (result.status === "not-found") {
    return (
      <Container>
        <p className="mb-4 text-neutral-500 text-xs">注文番号 {code}</p>
        <h1 className="mb-6 font-medium text-2xl text-neutral-900 tracking-tight">
          ご注文が見つかりませんでした
        </h1>
        <Link to="/dashboard" className="text-sm underline underline-offset-4">
          注文履歴に戻る
        </Link>
      </Container>
    );
  }
  if (result.status === "error") {
    return (
      <Container>
        <h1 className="mb-6 font-medium text-2xl text-neutral-900 tracking-tight">
          注文情報を取得できませんでした
        </h1>
        <p className="mb-6 text-neutral-600 text-sm">{result.message}</p>
        <Link to="/dashboard" className="text-sm underline underline-offset-4">
          注文履歴に戻る
        </Link>
      </Container>
    );
  }

  return <ReceiptBody order={result.order} />;
};

const ReceiptBody: FC<{ order: OrderDetailDTO }> = ({ order }) => {
  const slug = normalizeOrderCode(order.code);
  const logo = storeLogo(order.store);
  const items = flattenItemsForReceipt(order.itemGroups);

  return (
    <>
      <style>{printStyles}</style>

      {/* 画面表示時のみ出るツールバー (print:hidden) */}
      <div className="receipt-toolbar mx-auto mb-6 flex w-full max-w-3xl items-center justify-between px-5 pt-4 print:hidden">
        <Link to={`/orders/${slug}`} className="text-neutral-500 text-xs hover:text-neutral-900">
          ← 注文詳細
        </Link>
        <div className="flex flex-col items-end gap-1">
          <Button type="button" onClick={() => window.print()}>
            印刷する
          </Button>
          <p className="text-neutral-500 text-xs">印刷ダイアログから「PDFとして保存」も可能です</p>
        </div>
      </div>

      <article className="receipt-paper mx-auto max-w-3xl bg-white px-5 py-8 text-neutral-900 sm:px-10 sm:py-12 print:max-w-none print:px-8 print:py-8">
        <header className="mb-8 flex items-start justify-between gap-4">
          <div>
            <img src={logo.src} alt={logo.alt} className="h-8 w-auto sm:h-10" />
          </div>
          <div className="text-right text-sm">
            <p>領収書 #{slug}</p>
            <p className="text-neutral-600">購入日:{formatReceiptDate(order.createdAt)}</p>
          </div>
        </header>

        {/* 宛名手書き欄 (法人経理等が印刷後に手書きで埋める運用)。将来カスタム入力 UI を載せる余地。 */}
        <section className="mb-8">
          <p className="mb-1 text-neutral-500 text-xs">宛名</p>
          <div className="border-neutral-400 border-b py-2">
            <span className="text-neutral-300 text-xs print:hidden">
              ご記入欄 (印刷後に手書きでご記入ください)
            </span>
          </div>
        </section>

        {/* 商品行: スマホは縦積みカード、デスクトップ + 印刷時はテーブル。 */}
        <section className="border-neutral-300 border-t border-b py-3">
          <table className="hidden w-full text-sm sm:table print:table">
            <thead>
              <tr className="text-neutral-500 text-xs">
                <th className="pb-2 text-left font-normal">アイテム</th>
                <th className="pb-2 pl-6 text-right font-normal">
                  <span className="whitespace-nowrap">価格</span>{" "}
                  <span className="whitespace-nowrap">(税込)</span>
                </th>
                <th className="whitespace-nowrap pb-2 pl-6 text-right font-normal">数量</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {items.map((item) => (
                <ReceiptItemRow key={item.lineItemId} item={item} />
              ))}
            </tbody>
          </table>
          <ul className="divide-y divide-neutral-100 sm:hidden print:hidden">
            {items.map((item) => (
              <ReceiptItemCard key={item.lineItemId} item={item} />
            ))}
          </ul>
        </section>

        <section className="receipt-summary mt-4 ml-auto max-w-md space-y-2 text-sm">
          {order.discountJpy > 0 && (
            <SummaryRow label="ディスカウント" value={`- ${formatYen(order.discountJpy)}`} />
          )}
          <SummaryRow label="小計" value={formatYen(order.subtotalJpy)} />
          <SummaryRow
            label="送料"
            value={order.shippingJpy === 0 ? "無料" : formatYen(order.shippingJpy)}
          />
          <div className="border-neutral-300 border-t pt-2">
            <SummaryRow label="合計 (JPY)" value={formatYen(order.totalPriceJpy)} bold />
            {/* 適格請求書の必須項目: 税率ごとの対価の額 + 消費税額。サバキューは全商品 10% */}
            <SummaryRow label="10% 対象 (税込)" value={formatYen(order.totalPriceJpy)} muted />
            <SummaryRow label="うち消費税" value={formatYen(order.taxJpy)} muted />
          </div>
        </section>

        <footer className="receipt-footer mt-12 space-y-1 border-neutral-200 border-t pt-6 text-right text-neutral-700 text-sm">
          <p className="font-medium">{COMPANY_INFO.legalName}</p>
          <p>
            〒{COMPANY_INFO.postalCode}　{COMPANY_INFO.address}
          </p>
          <p>登録番号:{COMPANY_INFO.invoiceRegistrationNumber}</p>
        </footer>
      </article>
    </>
  );
};

const ReceiptItemRow: FC<{ item: OrderItemDTO }> = ({ item }) => (
  <tr>
    <td className="py-3 align-top">
      <div className="flex items-start gap-3">
        <img
          src={item.thumbnailUrl}
          alt={item.title}
          width={56}
          height={56}
          className="size-14 flex-shrink-0 rounded border border-neutral-200 bg-neutral-50 object-cover"
        />
        <div className="min-w-0">
          <p className="text-neutral-900 text-sm leading-snug">{item.title}</p>
          {item.variantTitle && (
            <p className="mt-0.5 text-neutral-500 text-xs">{item.variantTitle}</p>
          )}
        </div>
      </div>
    </td>
    <td className="whitespace-nowrap py-3 pl-6 text-right align-top">{formatYen(item.priceJpy)}</td>
    <td className="whitespace-nowrap py-3 pl-6 text-right align-top">{item.quantity}</td>
  </tr>
);

/** モバイル (sm 未満) で表示する商品カード。 */
const ReceiptItemCard: FC<{ item: OrderItemDTO }> = ({ item }) => (
  <li className="flex gap-3 py-3">
    <img
      src={item.thumbnailUrl}
      alt={item.title}
      width={48}
      height={48}
      className="size-12 flex-shrink-0 rounded border border-neutral-200 bg-neutral-50 object-cover"
    />
    <div className="min-w-0 flex-1 space-y-1.5">
      <p className="text-neutral-900 text-sm leading-snug">{item.title}</p>
      {item.variantTitle && <p className="text-neutral-500 text-xs">{item.variantTitle}</p>}
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-neutral-600 text-xs tabular-nums">
        <dt>価格 (税込)</dt>
        <dd className="text-right text-neutral-900">{formatYen(item.priceJpy)}</dd>
        <dt>数量</dt>
        <dd className="text-right">{item.quantity}</dd>
      </dl>
    </div>
  </li>
);

const SummaryRow: FC<{ label: string; value: string; bold?: boolean; muted?: boolean }> = ({
  label,
  value,
  bold,
  muted,
}) => (
  <div
    className={`flex items-baseline justify-between gap-3 ${
      bold ? "font-medium" : muted ? "text-neutral-500 text-xs" : ""
    }`}
  >
    <dt className={muted ? "" : "text-neutral-600"}>{label}</dt>
    <dd className={`tabular-nums ${muted ? "" : "text-neutral-900"}`}>{value}</dd>
  </div>
);

const printStyles = `
  @media print {
    /* SiteHeader / SiteFooter は data-print-hide 属性で確実に隠す (Tailwind クラス依存だと壊れやすい) */
    [data-print-hide] {
      display: none !important;
    }
    /* Layout の余白を除去 */
    body, main {
      background: white !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    .receipt-paper {
      box-shadow: none !important;
      max-width: none !important;
    }
    /* リンクの装飾は印刷物として邪魔なので除去 */
    a {
      color: inherit !important;
      text-decoration: none !important;
    }
    /* 商品行 / サマリ / フッタは途中で改ページしない (合計だけ次ページに飛ぶ事故を防ぐ) */
    tr,
    .receipt-summary,
    .receipt-footer {
      break-inside: avoid;
    }
    @page {
      margin: 12mm;
      size: A4;
    }
  }
`;
